import React, { useRef, useEffect, useState } from "react";
import styles from "./ai-rag-chat-modal.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { updateIsAIRagChatOpen } from "@src/reducers/your-reducer";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { Button } from "@src/components/atoms/buttons/button/button";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import closeIcon from "@src/assets/svg/close-icon.svg";
import refreshIcon from "@src/assets/svg/refresh.svg";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { useAIChat } from "./use-ai-chat";
import { ChatMessage } from "./chat-message";
import { ChatInput } from "./chat-input";
import { useI18N } from "@src/language/language-context";

const cssClassName = "ai-rag-chat-modal";

export const AIRagChatModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const userId = useSelector(
    (state: RootState) => state.yourSlice.authContext.userId,
  );
  const namespace = useSelector(
    (state: RootState) => state.yourSlice.authContext.customerNamespace,
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    input,
    setInput,
    isLoading,
    expandedThinking,
    expandedSources,
    sendMessage,
    resetConversation,
    toggleThinkingExpanded,
    toggleSourcesExpanded,
    updateMessageFeedback,
    selectedFiles,
    addFiles,
    removeFile,
  } = useAIChat({ userId, namespace });
  const isConversationEmpty = messages.length === 0;

  const texts = {
    subtitle: i18n("modules.aiRagChat.subtitle"),
    conversationHistory: i18n("modules.aiRagChat.conversationHistory"),
    newConversation: i18n("modules.aiRagChat.newConversation"),
    confirmResetTitle: i18n("modules.aiRagChat.confirmResetTitle"),
    confirmResetMessage: i18n("modules.aiRagChat.confirmResetMessage"),
    confirmResetAction: i18n("modules.aiRagChat.confirmResetAction"),
    cancelResetAction: i18n("modules.aiRagChat.cancelResetAction"),
    getStartedQuestionText: i18n("modules.aiRagChat.getStartedQuestionText"),
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCloseModal = () => {
    dispatch(updateIsAIRagChatOpen(false));
  };

  const handleOpenResetDialog = () => {
    if (isConversationEmpty) {
      return;
    }
    setShowResetDialog(true);
  };

  const handleCancelReset = () => {
    setShowResetDialog(false);
  };

  const handleConfirmReset = () => {
    resetConversation();
    setShowResetDialog(false);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__header`]}>
        <SubTitle text={texts.subtitle} />
        <IconButton fun={handleCloseModal} icon={closeIcon} />
      </div>

      <div className={styles[`${cssClassName}__actions-bar`]}>
        <p className={styles[`${cssClassName}__actions-title`]}>
          {texts.conversationHistory}
        </p>
        <ButtonWithIcon
          fun={handleOpenResetDialog}
          icon={refreshIcon}
          text={texts.newConversation}
          type="button"
          variant={["secondary", "rounded"]}
          disabled={isConversationEmpty}
        />
      </div>

      {showResetDialog && (
        <div className={styles[`${cssClassName}__dialog-backdrop`]}>
          <div className={styles[`${cssClassName}__dialog`]}>
            <p className={styles[`${cssClassName}__dialog-title`]}>
              {texts.confirmResetTitle}
            </p>
            <p className={styles[`${cssClassName}__dialog-message`]}>
              {texts.confirmResetMessage}
            </p>
            <div className={styles[`${cssClassName}__dialog-actions`]}>
              <Button
                fun={handleCancelReset}
                text={texts.cancelResetAction}
                variant="secondary"
              />
              <Button
                fun={handleConfirmReset}
                text={texts.confirmResetAction}
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles[`${cssClassName}__chat-container`]}>
        {messages.length === 0 ? (
          <div className={styles[`${cssClassName}__empty-state`]}>
            <p>{texts.getStartedQuestionText}</p>
          </div>
        ) : (
          messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              namespace={namespace}
              isThinkingExpanded={expandedThinking.has(message.id)}
              isSourcesExpanded={expandedSources.has(message.id)}
              onToggleThinking={() => toggleThinkingExpanded(message.id)}
              onToggleSources={() => toggleSourcesExpanded(message.id)}
              updateMessageFeedback={updateMessageFeedback}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        value={input}
        onChange={setInput}
        onSend={sendMessage}
        isLoading={isLoading}
        inputRef={inputRef}
        selectedFiles={selectedFiles}
        onAddFiles={addFiles}
        onRemoveFile={removeFile}
      />
    </div>
  );
};
